import React, {useCallback, useEffect, useState} from 'react';
import {RefreshControl, StyleSheet} from 'react-native';
import {useAppDispatch, useAppSelector} from '@buncha/config/redux/hooks';

import ActiveDriverOrderCell from '../../driverOrderCell/ActiveDriverOrderCell';
import {Analytics} from '../../../../../../src/config/analytics/analytics';
import {CustomFlatList} from '../../../../../../src/components/core/list';
import {CustomText} from '@buncha/components/core/text';
import {HBox} from '@buncha/components/core/view';
import {HomeScreens} from '@buncha/home/navigation/types';
import If from '@buncha/components/conditional/If';
import LoaderComponents from '@buncha/components/composites/CustomLoader/LoaderComponents';
import {Modules} from '@buncha/utils/types';
import NoOrderForDriver from '../../noOrderForDriver';
import {Task} from '@buncha/config/onfleet/types';
import {ToastType} from '@buncha/components/composites/notification/type';
import {homeActions} from '@buncha/home/reducer';
import {isWeb} from '../../../../../../src/utils/common';
import onfleetService from '@buncha/config/onfleet';
import {useColorThemeType} from '../../../../../../src/appStyles/hooks/theme';
import {useGetAddressForOrders} from '@buncha/driver/hooks/driverOrders';
import {useGetLocation} from '@buncha/utils/commonWithoutWeb';
import {useIsFocused} from '@react-navigation/native';
import {useToastMessage} from '@buncha/components/composites/notification';
import {useGetLocale} from '@buncha/localization/hooks';

interface RunOrdersProps {
  runId: number;
  setOrdersCount: React.Dispatch<React.SetStateAction<number>>;
  setLoading?: React.Dispatch<React.SetStateAction<boolean>>;
  updateUsersData: (users: any) => void;
}

const RunOrders: React.FC<RunOrdersProps> = props => {
  const runId = props?.runId || 0;
  const theme = useColorThemeType('main');
  const [showSuccessToast] = useToastMessage(ToastType.Success);
  const isFocused = useIsFocused();
  const {coordinates, getLocationWithPermission} = useGetLocation();
  const {addresses, getAddressForOrders, ordersLoading} =
    useGetAddressForOrders();
  const {body} = useGetLocale(Modules.driver, HomeScreens.Driver) ?? {};

  const driverTasks = useAppSelector(gState => gState.home.driverTasks);
  const appDispatch = useAppDispatch();

  const [loading, setLoading] = useState(true);

  const renderItem = useCallback(
    ({item, index}: {item: Task; index: number}) => {
      const order = addresses[item.id];
      return (
        <ActiveDriverOrderCell order={order} task={item} index={index + 1} />
      );
    },
    [addresses],
  );

  const fetchOnfleetData = useCallback(() => {
    setLoading(true);
    onfleetService.fetchTasks().then(response => {
      appDispatch(homeActions.setDriverTasks(response));
      const orderIds =
        response
          ?.map(task => ({
            orderId: Number(task?.metadata?.orderId),
            runId: Number(task?.metadata?.runId),
          }))
          ?.filter(task => Boolean(task?.orderId) && Boolean(task?.runId)) ??
        [];
      getAddressForOrders(orderIds);
      setLoading(false);
    });
    onfleetService.fetchActiveTask().then(response => {
      appDispatch(homeActions.setActiveTask(response));
    });
  }, [appDispatch, getAddressForOrders]);

  useEffect(() => {
    getLocationWithPermission();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchOnfleetData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused]);

  useEffect(() => {
    props?.setOrdersCount(driverTasks?.length ?? 0);
    if (driverTasks?.length === 0 && !ordersLoading)
      showSuccessToast('All assigned tasks completed!');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverTasks?.length]);

  useEffect(() => {
    if (props?.setLoading) props?.setLoading(loading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if ((runId && isFocused && coordinates) || isWeb()) {
      props?.setOrdersCount(driverTasks?.length ?? 0);

      Analytics.eventWithProps('Run wise orders for driver', {
        runId: runId,
        userType: 'driver',
        ordersCount: driverTasks?.length,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocused, coordinates]);
  if (loading || ordersLoading)
    return <LoaderComponents showBottomText={false} />;

  return (
    <CustomFlatList
      ListEmptyComponent={<NoOrderForDriver />}
      contentContainerStyle={styles.content}
      refreshControl={
        <RefreshControl
          refreshing={loading}
          onRefresh={fetchOnfleetData}
          tintColor={theme?.background.gray[100]}
          style={styles.refreshControl}
        />
      }
      ListHeaderComponent={
        <If condition={driverTasks?.length}>
          <HBox
            style={[
              styles.infoBox,
              {
                backgroundColor: theme?.background.yellow[100],
              },
            ]}>
            <CustomText
              style={[
                styles.disclaimerText,
                styles.iconText,
                {
                  color: theme?.text?.blue?.[300],
                },
              ]}>
              {body?.onfleetSorting?.[0]}
            </CustomText>
            <CustomText
              style={[
                styles.disclaimerText,
                {
                  color: theme?.text?.blue?.[300],
                },
              ]}>
              {body?.onfleetSorting?.[1]}
            </CustomText>
          </HBox>
        </If>
      }
      showsVerticalScrollIndicator={false}
      renderItem={renderItem}
      data={driverTasks}
    />
  );
};

export default React.memo(RunOrders);

const styles = StyleSheet.create({
  refreshControl: {zIndex: 100},
  spinnerContainer: {
    justifyContent: 'center',
    flex: 1,
  },
  content: {
    flexGrow: 1,
  },
  disclaimerBox: {
    padding: 5,
    borderRadius: 5,
  },
  infoBox: {
    paddingVertical: 10,
    paddingHorizontal: 15,
    borderRadius: 10,
    marginVertical: 10,
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: 5,
    paddingRight: 20,
  },
  disclaimerText: {
    textAlign: 'left',
  },
  iconText: {
    marginTop: 3,
  },
});
